<script lang="ts">
  import {DropdownButton} from '@/components/button';
  import {getDownloadOptionsContext} from '@/contexts/download';
  import {isMobile, user} from '@/store';
  import type {Video} from '@/types/project';
  import {download} from '@/utils/common';
  import {onMount} from 'svelte';
  import Swal from 'sweetalert2';
  import {downloadDiscussionGuide, regenerateDiscussionGuide, serializedDiscussionGuide} from './DiscussionGuide';

  export let selectedVideo: Video;

  const type = 'Discussion Guide';
  const downloadOptions = getDownloadOptionsContext();
  let isLoadingDiscussionGuide = true;
  let discussionGuide: {type: string; content: string}[] = [];
  let textDiscussionGuide = '';

  $: isDownloadable = !isLoadingDiscussionGuide && textDiscussionGuide !== '';

  async function regenerate() {
    if (!selectedVideo) throw 'selectedVideo is null';
    isLoadingDiscussionGuide = true;
    textDiscussionGuide = '';
    discussionGuide = [];

    await regenerateDiscussionGuide(selectedVideo.id)
      .then(response => {
        textDiscussionGuide = response.raw;
        discussionGuide = response.discussionGuide;
      })
      .catch(error => {
        console.log(error);
        Swal.fire({
          position: 'bottom-end',
          icon: 'error',
          title: 'Discussion Guide Failed to Download',
          showConfirmButton: false,
          timer: 10000,
          toast: true
        });
      })
      .finally(() => {
        isLoadingDiscussionGuide = false;
      });
  }

  onMount(async () => {
    if (!selectedVideo) throw 'selectedVideo is null';

    await downloadDiscussionGuide(selectedVideo.id)
      .then(response => {
        textDiscussionGuide = response.raw;
        discussionGuide = response.discussionGuide;
      })
      .catch(error => {
        console.log(error);
        Swal.fire({
          position: 'bottom-end',
          icon: 'error',
          title: 'Discussion Guide Failed to Download',
          showConfirmButton: false,
          timer: 10000,
          toast: true
        });
      })
      .finally(() => {
        isLoadingDiscussionGuide = false;
      });
  });

  const handleDownload = async (extension: 'txt' | 'html' | 'pdf' = 'txt') => {
    const filename = `[${type}] ${selectedVideo.title}`;
    const text = serializedDiscussionGuide(textDiscussionGuide);
    await download(filename, text, type, extension);
  };
</script>

<div class="h-full w-full">
  <div class="flex h-16 w-full items-center justify-between border-b border-b-gray-200 px-4 dark:border-b-zinc-600">
    {#if !$isMobile}
      <h2 class="text-md w-full font-bold text-gray-900">Discussion Guide</h2>
    {/if}
    <div class="flex w-full justify-center space-x-2 py-2 lg:justify-end lg:py-0">
      {#if $user.isImpersonated || $user.isAdmin}
        <button
          type="button"
          on:click={() => regenerate()}
          class="w-full rounded-md px-3 lg:w-auto py-1{isLoadingDiscussionGuide
            ? 'disabled cursor-progress bg-zinc-400 text-white'
            : 'hoversermonbg border border-primary bg-white text-primary hover:bg-primary/10'}"
          disabled={isLoadingDiscussionGuide}><i class="fa-solid fa-repeat" />&nbsp; Regenerate</button
        >
      {/if}
      <DropdownButton
        on:click={() => handleDownload()}
        on:optionClick={option => handleDownload(option.detail.value)}
        disabled={!isDownloadable}
        options={downloadOptions}
      >
        <span class="flex items-center gap-x-2">
          <i class="fa-solid fa-download" />Download
        </span>
      </DropdownButton>
    </div>
  </div>
  <div class="h-[calc(100%-4rem)] w-full overflow-auto p-4">
    {#if isLoadingDiscussionGuide}
      <div class="flex h-full w-full justify-center">
        <div class="flex flex-col items-center gap-4 pt-10">
          <div
            class="spinner h-8 w-8 !border-zinc-200 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          />
          <h1 class="text-3xl font-bold text-zinc-400 dark:text-zinc-400">Loading Discussion Guide...</h1>
        </div>
      </div>
    {:else if discussionGuide != null}
      <div class="flex h-full w-full justify-center">
        <div class="h-full w-full max-w-[40rem]">
          <div class="grid">
            {#each discussionGuide as discussion, i}
              {#if discussion.type === 'header'}
                <span class="text-lg font-bold lg:text-3xl {i == 0 ? 'pb-6' : 'pb-4 pt-10'}"
                  >{@html discussion.content}</span
                >
              {:else if discussion.type === 'list'}
                <span class="ml-5 text-justify leading-loose">{@html discussion.content}</span>
              {:else if discussion.type === 'break'}
                <br />
              {:else}
                <span
                  class="text-justify leading-loose {i === discussionGuide.length - 1 ? 'pb-6' : ''}"
                  style="white-space: pre-line">{@html discussion.content}</span
                >
              {/if}
            {/each}
          </div>
        </div>
      </div>
    {:else}
      <div class="flex h-full w-full items-center justify-center">
        <div class="flex flex-col items-center justify-center gap-4">
          <h1 class="pt-10 text-3xl font-bold text-zinc-400 dark:text-zinc-400">No Discussion Guide Found</h1>
        </div>
      </div>
    {/if}
  </div>
</div>
